/** @format */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import {
    SimCardServiceTypeAllowedParamCollection,
    SimCardServiceTypeAllowedParamMap,
    SimCardServiceTypeItem,
    SimCardServiceTypeParam,
} from '../../../../interfaces/collections/SimCardServiceInterface';
import I18n from '../../../../components/Translate/I18n';
import CustomTextButtom from '../../../../components/Buttons/CustomTextButton';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import { SimpleSelect } from '../../../../components/Adescom/SimpleSelect';
import { FeatureVerbTypeMap, getFeatureVerbName } from '../../../../modules/features/FeaturesTypes';
import { OplSimCardServiceTypeParamItemSdgForm } from './OplSimCardServiceTypeParamItemSdgForm';
import { usePlatformResellerHeaders, usePlatformResellerList } from '../../../../modules/lib/hooks/usePlatformResellerHooks';
import { ItemLoaders } from '../../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../../helpers/FormGeneral';
import { ErrorMultiObject } from '../../../../interfaces/api/Error';
import { notificationConstants } from '../../../../modules/notifications/notifications';
import Button from '@material-ui/core/Button';
import { ReactComponent as Cancel } from '../../../../assets/themes/OPL/img/icons/Cancel.svg';

type OplSimCardServiceTypeParamItemAddType = {
    simCardServiceType: SimCardServiceTypeItem;
    simCardServiceTypeAllowedParamsCollection: SimCardServiceTypeAllowedParamCollection;
    open: boolean;
    closeCallback(): void;
    reloadList(): void;
};

export const OplSimCardServiceTypeParamItemSdgAdd: FunctionComponent<OplSimCardServiceTypeParamItemAddType> = ({
    simCardServiceType,
    simCardServiceTypeAllowedParamsCollection,
    open,
    closeCallback,
    reloadList,
}): React.ReactElement => {
    const dispatch = useDispatch();
    const [platformResellerId, setPlatformResellerId] = useState<number | null>(null);
    const platformResellerList = usePlatformResellerList([]);
    const platformResellerHeader = usePlatformResellerHeaders(platformResellerList);
    const [serviceParameterActivate, setServiceParameterActivate] = useState<any>(null);
    const [serviceParameterDeactivate, setServiceParameterDeactivate] = useState<any>(null);
    const [serviceParameterModify, setServiceParameterModify] = useState<any>(null);

    const confirmCallback = (): void => {
        const request: Array<SimCardServiceTypeParam> = [];
        if (serviceParameterActivate) {
            request.push(addNewConfiguration(serviceParameterActivate, FeatureVerbTypeMap.ACTIVATE));
        }
        if (serviceParameterDeactivate) {
            request.push(addNewConfiguration(serviceParameterDeactivate, FeatureVerbTypeMap.DEACTIVATE));
        }
        if (serviceParameterModify) {
            request.push(addNewConfiguration(serviceParameterModify, FeatureVerbTypeMap.MODIFY));
        }
        if (request.length > 0) {
            const item = new ItemLoaders(dispatch);
            item.multiPostWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_params')),
                'post_sim_card_service_type_params_custom',
                request,
                (data: Array<{ status: string; value?: SimCardServiceTypeParam; reason?: ErrorMultiObject }>): void => {
                    let isAllOk = true;
                    let lastError: string | null = null;
                    for (let i = 0; i < data.length; i++) {
                        if (data[i].status === 'rejected' && data[i].reason) {
                            isAllOk = false;
                            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                            // @ts-ignore
                            lastError = data[i].reason.message.message;
                        }
                    }

                    reloadList();
                    closeCallback();
                    if (!isAllOk) {
                        dispatch({
                            type: notificationConstants.ERROR,
                            error: lastError,
                        });
                    }
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    };

    /**
     * @todo change base-api to adescom-api
     * @param params
     * @param operationVerbId
     */
    const addNewConfiguration = (params: { [key: string]: SimCardServiceTypeAllowedParamMap }, operationVerbId: number): SimCardServiceTypeParam => {
        const configuration = {} as SimCardServiceTypeParam;
        configuration.platformResellerId = platformResellerId;
        configuration.platformReseller = platformResellerId ? '/base-api/api/platform/platform_resellers/' + platformResellerId : null;
        configuration.verb = operationVerbId;
        configuration.simCardServiceType = '/base-api/api/platform/sim_card_service_types/' + simCardServiceType.id;
        configuration.parameters = JSON.stringify(params);
        return configuration;
    };

    const setPlatformResellerCallback = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        setPlatformResellerId(parseInt(event.target.value));
    };

    const getFormValues = (operationVerbId: number, formValues: any): void => {
        if (operationVerbId === FeatureVerbTypeMap.ACTIVATE) {
            setServiceParameterActivate(formValues);
        }
        if (operationVerbId === FeatureVerbTypeMap.DEACTIVATE) {
            setServiceParameterDeactivate(formValues);
        }
        if (operationVerbId === FeatureVerbTypeMap.MODIFY) {
            setServiceParameterModify(formValues);
        }
    };

    return (
        <Dialog open={open} maxWidth="md">
            <DialogTitle>
                <GridContainer justifyContent={'space-between'}>
                    <h4>
                        <I18n trans="prov:feature.add_new_configuration" />
                    </h4>
                    <Button onClick={closeCallback} color="primary">
                        <Cancel width={24} height={24} />
                    </Button>
                </GridContainer>
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.choose_partner"
                            required={false}
                            elements={platformResellerHeader ?? []}
                            fieldName="platformResellerId"
                            dispatchName={'none'}
                            selectedValue={platformResellerId ?? undefined}
                            callback={setPlatformResellerCallback}
                        />
                        <div style={{ fontSize: '11px' }}>
                            <I18n trans="prov:feature.no_choose_partner" />
                        </div>
                        {simCardServiceTypeAllowedParamsCollection['hydra:member'].map((item) => {
                            return (
                                <React.Fragment key={'add_params_' + item.id}>
                                    <hr />
                                    <GridContainer>
                                        <GridItem xs={3}>
                                            <Typography variant="subtitle2">
                                                <I18n trans="prov:feature.operation_name" />
                                            </Typography>
                                        </GridItem>
                                        <GridItem xs={3}>
                                            <Typography variant="subtitle2">
                                                <I18n trans={'prov:feature.' + getFeatureVerbName(item.operationVerbId)} />
                                            </Typography>
                                        </GridItem>
                                        <GridItem xs={6} />
                                        <GridItem xs={12}>
                                            <OplSimCardServiceTypeParamItemSdgForm
                                                simCardServiceTypeAllowedParam={item}
                                                getFormValues={(formValues: any): void => getFormValues(item.operationVerbId, formValues)}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </React.Fragment>
                            );
                        })}
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="prov:feature.add_new" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
